import Vue from 'vue';
import Router from 'vue-router';
import userRole from '@/services/userRoleService';
import AppService from '@/services/app.service';
import store from '@/store/Index';

function getUserFromStorage() {
  try {
    const user = localStorage.getItem('user');
    if (!user || user === "undefined") {
      return null;
    }
    return JSON.parse(user);
  } catch (error) {
    console.error('Fehler beim Laden des Users:', error);
    return null;
  }
}

function isLoggedIn(user) {
  return !!user;
}

function redirectTo(next, path) {
  next(path);
}

// Cache für ensureLicenceLoaded Promise
let licenceLoadPromise = null;

async function handleAccessControl(to, next, user) {

  if (!licenceLoadPromise) {
    licenceLoadPromise = AppService.ensureLicenceLoaded().catch((error) => {
      // Nach einem Fehlschlag zuruecksetzen, sonst bleibt die rejected Promise
      // fuer die App-Lifetime haengen und jede Navigation scheitert bis zum Reload.
      licenceLoadPromise = null;
      throw error;
    });
  }
  await licenceLoadPromise;

  const isProduction = process.env.NODE_ENV === 'production';
  const isCreatorSubActive = store.state.main.licence.result.subscription; // true || false
  const isTester = AppService.isTester();
  const hasProduct = AppService.hasProduct(AppService.getProducts(to.meta.product));

  if (isProduction && to.name !== 'checkout' && !isCreatorSubActive && !isTester) {
    return user.role === 'ROLE_END_USER' ? redirectTo(next, '/install') : redirectTo(next, '/checkout');
  }

  if (isProduction && hasProduct) {
    next();
  } else if (!isProduction) {
    next();
  } else {
    redirectTo(next, '/checkout');
  }
}

// Router guard
function guard(to, from, next) {

  try {
    // Allow ageVerification with code parameter even when not logged in
    if (to.name === 'ageverification' && to.query.code) {
      next();
      return;
    }

    const user = getUserFromStorage();
    if (!user) {
      // '/' hat keine Route (kein passender Eintrag, kein Catch-All). Vorher
      // lief der Guard damit ins Leere: die Navigation brach ab und der Nutzer
      // sah eine leere Seite plus "Redirected ... via a navigation guard".
      next('/login');
      return;
    }

    if (isLoggedIn(user) && userRole.hasRole(to.meta.roles)) {
      if (from.path === to.path && !from.meta.reload) {
        to.meta.reload = true;
        next();
      } else {
        handleAccessControl(to, next, user);
      }
    } else {
      next('/login');
    }
  } catch (error) {
    next('/login');
  }
}



Vue.use(Router)

const router = new Router({

  mode: 'history',
  base: process.env.BASE_URL,
  routes: [
    // Authentication routes
    {
      path: '/login',
      name: 'login',
      component: () => import('./views/global/login.vue'),
      meta: { label: 'nav.appbar.login' },
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('./views/global/register.vue'),
      meta: { label: 'nav.appbar.register' },
    },
    {
      path: '/users/:id/activate',
      name: 'activate',
      component: () => import('./views/global/activate.vue'),
      meta: { label: 'auth.activate.title' },
    },
    {
      path: '/passwordReset',
      name: 'passwordReset',
      component: () => import('./views/global/passwordReset.vue'),
      meta: { label: 'auth.password_reset.title' },
    },
    {
      path: '/users/:id/reset',
      name: 'newPassword',
      component: () => import('./views/global/newPassword.vue'),
      meta: { label: 'auth.new_password.title' },
    },

    // Global routes
    {
      path: '/install',
      name: 'install',
      component: () => import('./views/global/install.vue'),
      meta: { label: 'install.title' },
    },
    {
      path: '/Datenschutzerklaerung',
      name: 'Datenschutzerklaerung',
      component: () => import('./views/global/Datenschutzerklaerung.vue'),
      meta: { label: 'nav.footer.privacy' }
    },
    {
      path: '/Nutzungsbedingungen',
      name: 'Nutzungsbedingungen',
      component: () => import('./views/global/Nutzungsbedingungen.vue'),
      meta: { label: 'nav.footer.terms' }
    },
    {
      path: '/Impressum',
      name: 'Impressum',
      component: () => import('./views/global/Impressum.vue'),
      meta: { label: 'nav.footer.imprint' }
    },
    {
      path: '/content-removal',
      name: 'inhalte-melden',
      component: () => import('./views/global/complaintPolicy.vue'),
      meta: { label: 'nav.footer.report_content' }
    },
    {
      path: '/depicture-removal',
      name: 'abbildungen-melden',
      component: () => import('./views/global/removalPolicy.vue'),
      meta: { label: 'nav.footer.report_image' }
    },
    {
      path: '/videos/:id',
      name: 'videoTeilen',
      component: () => import('./views/global/sharedVideo.vue'),
      meta: { label: 'nav.sidebar.shared_video' },
    },
    {
      path: '/explicitwarning',
      name: 'Ab 18',
      component: () => import('./views/global/explicitwarning.vue'),
      meta: { label: 'explicit_warning.title' }
    },

    // User routes
    {
      path: '/home',
      name: 'Dashboard',
      component: () => import('./views/user/user_dashboard.vue'),
      meta: {
        roles: ['ROLE_TENANT', 'ROLE_END_USER', 'ROLE_USER'],
        product: 'Starter',
        label: 'nav.sidebar.my_site'
      },
      beforeEnter: guard
    },
    {
      path: '/video/:id',
      name: 'video',
      component: () => import('./views/user/video.vue'),
      meta: {
        roles: ['ROLE_TENANT', 'ROLE_END_USER', 'ROLE_USER'],
        product: 'Starter',
        label: 'nav.sidebar.videos'
      },
      beforeEnter: guard
    },
    // {
    //   path: '/stream/:id',
    //   name: 'stream',
    //   component: () => import('./views/user/liveStream.vue'),
    //   meta: {
    //     roles: ['ROLE_TENANT', 'ROLE_END_USER', 'ROLE_USER'],
    //     product: 'Premium'
    //   },
    //   beforeEnter: guard 
    // },
    // {
    //   path: '/favorits',
    //   name: 'Favoriten',
    //   component: () => import('./views/user/favorits.vue'),
    //   meta: {
    //     roles: ['ROLE_TENANT', 'ROLE_END_USER', 'ROLE_USER'],
    //     product: 'Starter'
    //   },
    //   beforeEnter: guard 
    // },
    // {
    //   path: '/live-streams',
    //   name: 'live-streams',
    //   component: () => import('./views/user/live-streams.vue'),
    //   meta: {
    //     roles: ['ROLE_TENANT', 'ROLE_END_USER', 'ROLE_USER'],
    //     product: 'Premium'
    //   },
    //   beforeEnter: guard 
    // },
    {
      path: '/playlist/:id',
      name: 'playlist',
      component: () => import('./views/user/playlist.vue'),
      meta: {
        roles: ['ROLE_TENANT', 'ROLE_END_USER', 'ROLE_USER'],
        product: 'Starter',
        label: 'nav.sidebar.playlists'
      },
      beforeEnter: guard
    },
    {
      path: '/playlists',
      name: 'playlists',
      component: () => import('./views/user/playlists.vue'),
      meta: {
        roles: ['ROLE_TENANT', 'ROLE_END_USER', 'ROLE_USER'],
        product: 'Starter',
        label: 'nav.sidebar.playlists'
      },
      beforeEnter: guard
    },
    {
      path: '/category',
      name: 'category',
      component: () => import('./views/user/categories.vue'),
      meta: {
        roles: ['ROLE_TENANT', 'ROLE_END_USER', 'ROLE_USER'],
        product: 'Starter',
        label: 'nav.sidebar.categories'
      },
      beforeEnter: guard
    },
    {
      path: '/userprofil',
      name: 'User Profil',
      component: () => import('./views/user/user_profil.vue'),
      meta: {
        roles: ['ROLE_END_USER', 'ROLE_USER', 'ROLE_TENANT'],
        product: 'Starter',
        label: 'nav.sidebar.profile'
      },
      beforeEnter: guard
    },
    {
      path: '/danke',
      name: 'Thank you',
      component: () => import('./views/user/thankyou.vue'),
      meta: {
        roles: ['ROLE_END_USER', 'ROLE_USER', 'ROLE_TENANT'],
        product: 'Starter',
        label: 'user.thankyou.title'
      },
      beforeEnter: guard
    },
    {
      path: '/ablehnung',
      name: 'Ablehnung',
      component: () => import('./views/user/denied.vue'),
      meta: {
        roles: ['ROLE_END_USER', 'ROLE_USER', 'ROLE_TENANT'],
        product: 'Starter',
        label: 'user.denied.title'
      },
      beforeEnter: guard
    },

    // Admin routes
    {
      path: '/Admin',
      name: 'Dashboard',
      component: () => import('./views/admin/admin_dashboard.vue'),
      meta: {
        roles: ['ROLE_TENANT'],
        product: 'Starter',
        label: 'nav.sidebar.dashboard'
      },
      beforeEnter: (to, from, next) => {
        guard(to, from, next)
        store.dispatch('fetch', { endpoint: '/api/v1/config', state: 'config' })
      }
    },
    {
      path: '/user',
      name: 'Benutzer Übersicht',
      component: () => import('./views/admin/user_overview.vue'),
      meta: {
        roles: ['ROLE_TENANT'],
        product: 'Starter',
        label: 'nav.sidebar.users'
      },
      beforeEnter: guard
    },
    // ******************
    // Under Construction
    // ******************
    // {
    //   path: '/chats',
    //   name: 'nachrichten',
    //   component: () => import('./views/admin/chats.vue'),
    //   meta: {
    //     roles: ['ROLE_TENANT', 'ROLE_END_USER', 'ROLE_USER'],
    //     product: 'Starter'
    //   },
    //   beforeEnter: guard
    // },
    {
      path: '/einstellungen',
      name: 'Einstellungen',
      component: () => import('./views/admin/einstellungen.vue'),
      meta: {
        roles: ['ROLE_TENANT'],
        product: 'Starter',
        label: 'nav.sidebar.settings'
      },
      beforeEnter: guard
    },
    {
      path: '/video-upload',
      name: 'Neues Video hochladen',
      component: () => import('./views/admin/video-upload.vue'),
      meta: {
        roles: ['ROLE_TENANT'],
        product: 'Starter',
        label: 'nav.sidebar.video_upload'
      },
      beforeEnter: (to, from, next) => {
        guard(to, from, next)
        store.dispatch('fetch', { endpoint: '/api/v1/config', state: 'config' })
      }
    },
    {
      path: '/videos',
      name: 'Alle Videos',
      component: () => import('./views/admin/videos.vue'),
      meta: {
        roles: ['ROLE_TENANT'],
        product: 'Starter',
        label: 'nav.sidebar.videos'
      },
      beforeEnter: guard
    },
    {
      path: '/Playlisten',
      name: 'Playlisten',
      component: () => import('./views/admin/playlists.vue'),
      meta: {
        roles: ['ROLE_TENANT'],
        product: 'Starter',
        label: 'nav.sidebar.playlists'
      },
      beforeEnter: (to, from, next) => {
        guard(to, from, next)
        store.dispatch('fetch', { endpoint: '/api/v1/playlists', state: 'playlists' })
      }
    },
    {
      path: '/video/:id/stats',
      name: 'videoStats',
      component: () => import('./views/admin/videoStats.vue'),
      meta: {
        roles: ['ROLE_TENANT'],
        product: 'Starter',
        label: 'nav.sidebar.stats'
      },
      beforeEnter: guard
    },
    {
      path: '/categories',
      name: 'categories',
      component: () => import('./views/admin/categories.vue'),
      meta: {
        roles: ['ROLE_TENANT'],
        product: 'Starter',
        label: 'nav.sidebar.categories'
      },
      beforeEnter: guard
    },
    {
      path: '/stats',
      name: 'Statistik',
      component: () => import('./views/admin/stats.vue'),
      meta: {
        roles: ['ROLE_TENANT'],
        product: 'Starter',
        label: 'nav.sidebar.stats'
      },
      beforeEnter: (to, from, next) => {
        guard(to, from, next)
        store.dispatch('fetch', { endpoint: '/api/v1/config', state: 'config' })
        store.dispatch('fetch', { endpoint: '/api/v1/stats', state: 'stats' })
        store.dispatch('fetch', { endpoint: '/api/v1/videos', state: 'videos' })
      }
    },
    // Live Streaming
    // {
    //   path: '/streams',
    //   name: 'Streams',
    //   component: () => import('./views/admin/streams.vue'),
    //   meta: {
    //     roles: ['ROLE_TENANT'],
    //     product: 'Premium'
    //   },
    //   beforeEnter: (to, from, next) => {
    //     guard(to, from, next)
    //     store.dispatch('fetch', { endpoint: '/api/v1/streams', state: 'streams'})
    //   }
    // },
    // {
    //   path: '/stream_bearbeiten',
    //   name: 'Stream bearbeiten',
    //   component: () => import('./views/admin/stream_bearbeiten.vue'),
    //   meta: {
    //     roles: ['ROLE_TENANT'],
    //     product: 'Premium'
    //   },
    //   beforeEnter: guard 
    // },
    // {
    //   path: '/stream_start-options',
    //   name: 'Stream Start - Einstellungen',
    //   component: () => import('./views/admin/stream_start-options.vue'),
    //   meta: {
    //     roles: ['ROLE_TENANT'],
    //     product: 'Premium'
    //   },
    //   beforeEnter: guard 
    // },
    // {
    //   path: '/stream_backstage',
    //   name: 'Stream Start - Backstage',
    //   component: () => import('./views/admin/stream_backstage.vue'),
    //   meta: {
    //     roles: ['ROLE_TENANT'],
    //     product: 'Premium'
    //   },
    //   beforeEnter: guard 
    // },
    // {
    //   path: '/stream_erstellen',
    //   name: 'Stream erstellen',
    //   component: () => import('./views/admin/stream_erstellen.vue'),
    //   meta: {
    //     roles: ['ROLE_TENANT'],
    //     product: 'Premium'
    //   },
    //   beforeEnter: guard 
    // },
    {
      path: '/admin_profil',
      name: 'Admin Profil',
      component: () => import('./views/admin/admin_profil.vue'),
      meta: {
        roles: ['ROLE_TENANT'],
        product: 'Starter',
        label: 'nav.sidebar.profile_settings'
      },
      beforeEnter: guard
    },
    // {
    //   path: '/notification',
    //   name: 'notification',
    //   component: () => import('./views/admin/notification.vue'),
    //   meta: {
    //     roles: ['ROLE_TENANT'],
    //     product: 'Starter'
    //   },
    //   beforeEnter: guard 
    // },
    {
      path: '/checkout',
      name: 'checkout',
      component: () => import('./views/admin/finalCheckout.vue'),
      meta: {
        roles: ['ROLE_TENANT'],
        product: 'Starter',
        label: 'checkout.title'
      },
    },
  ]
});

router.afterEach(to => {
  AppService.addComponents(router);
});

router.guard = guard;

export default router;

